// ============================================
// Contacto — Página de contacto y atención (/contacto)
// Layout 50/50: foto a la izquierda, panel cream a la derecha con datos,
// acceso al chat y formulario con labels flotantes (mismo estilo que login).
// Los datos directos del fundador los entrega el backend solo a distribuidoras
// con sesión iniciada (no viven en el bundle del front).
// ============================================

import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import FloatingInput from '../components/account/FloatingInput';
import LoginModal from '../components/account/LoginModal';
import { MEDUSA_API_URL, MEDUSA_API_KEY } from 'services/medusa/config';
import { useCustomer } from 'context/CustomerContext';
import { getMyDistributorStatus, getFounderContact } from 'services/medusa/distributor';
import SEO from '../components/SEO';
import './Contacto.css';

const EMPTY_FORM = { name: '', email: '', phone: '', message: '' };

const FloatingTextarea = ({ name, label, value, onChange, error, required }) => {
  const [isFocused, setIsFocused] = useState(false);
  const isActive = isFocused || (value && value.length > 0);
  return (
    <div className={`floating-input floating-input--textarea ${isActive ? 'is-active' : ''} ${error ? 'has-error' : ''}`}>
      <textarea
        name={name}
        value={value}
        onChange={onChange}
        onFocus={() => setIsFocused(true)}
        onBlur={() => setIsFocused(false)}
        rows={3}
        required={required}
        className="floating-input__field"
      />
      <label className="floating-input__label">
        {label}
        {required && <span className="floating-input__required">*</span>}
      </label>
      {error && <span className="floating-input__error">{error}</span>}
    </div>
  );
};

const Contacto = () => {
  const { customer, isAuthenticated, loading, token } = useCustomer();
  const [distStatus, setDistStatus] = useState(null);
  const [statusLoading, setStatusLoading] = useState(true);
  const [showLogin, setShowLogin] = useState(false);
  const [founder, setFounder] = useState(null);

  const [form, setForm] = useState(EMPTY_FORM);
  const [errors, setErrors] = useState({});
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [sendError, setSendError] = useState('');

  useEffect(() => {
    if (!token) {
      setDistStatus(null);
      setFounder(null);
      setStatusLoading(false);
      return;
    }
    let cancelled = false;
    setStatusLoading(true);
    getMyDistributorStatus(token)
      .then(async (s) => {
        if (cancelled) return;
        setDistStatus(s);
        if (s?.is_distributor) {
          const { contact } = await getFounderContact(token);
          if (!cancelled) setFounder(contact);
        } else {
          setFounder(null);
        }
      })
      .catch(() => {})
      .finally(() => { if (!cancelled) setStatusLoading(false); });
    return () => { cancelled = true; };
  }, [token]);

  // Si hay sesión, prellenamos nombre/correo/teléfono (sin pisar lo escrito).
  useEffect(() => {
    if (!customer) return;
    setForm((f) => ({
      ...f,
      name: f.name || [customer.first_name, customer.last_name].filter(Boolean).join(' '),
      email: f.email || customer.email || '',
      phone: f.phone || customer.phone || '',
    }));
  }, [customer]);

  const isDistributor = Boolean(distStatus?.is_distributor);
  const checking = loading || statusLoading;

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
    if (errors[name]) setErrors((er) => ({ ...er, [name]: '' }));
  };

  const validate = () => {
    const er = {};
    if (!form.name.trim()) er.name = 'Escribe tu nombre';
    if (!form.email.trim()) er.email = 'Escribe tu correo';
    else if (!/^\S+@\S+\.\S+$/.test(form.email.trim())) er.email = 'Correo no válido';
    if (!form.message.trim()) er.message = 'Escribe tu mensaje';
    setErrors(er);
    return Object.keys(er).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSendError('');
    if (!validate()) return;
    setSending(true);
    try {
      // Llega por correo a hello@ (backend Medusa → /store/contact).
      const res = await fetch(`${MEDUSA_API_URL}/store/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-publishable-api-key': MEDUSA_API_KEY,
        },
        body: JSON.stringify({
          name: form.name.trim(),
          email: form.email.trim(),
          phone: form.phone.trim(),
          message: form.message.trim(),
          is_distributor: !!isDistributor,
        }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || `HTTP ${res.status}`);
      }
      setSent(true);
      setForm(EMPTY_FORM);
    } catch (err) {
      console.error('Error enviando contacto:', err);
      setSendError('No pudimos enviar tu mensaje. Intenta de nuevo en un momento o escríbenos por el chat.');
    } finally {
      setSending(false);
    }
  };

  const openChat = () => window.dispatchEvent(new Event('phys:open-chat'));

  const renderDistributorBlock = () => {
    if (checking) {
      return <p className="ct-direct__note">Verificando tu sesión…</p>;
    }
    if (isAuthenticated && isDistributor) {
      if (!founder) return <p className="ct-direct__note">Cargando datos…</p>;
      return (
        <div className="ct-direct__card">
          <p className="ct-direct__name">{founder.name}. <span>{founder.role}</span></p>
          <p>
            WhatsApp:{' '}
            <a href={founder.whatsapp_url} target="_blank" rel="noopener noreferrer">{founder.whatsapp}</a>
          </p>
          <p>
            Mail: <a href={`mailto:${founder.email}`}>{founder.email}</a>
          </p>
        </div>
      );
    }
    if (isAuthenticated) {
      return (
        <p className="ct-direct__note">
          Esta línea es exclusiva para socias distribuidoras.{' '}
          <Link to="/socios-y-distribuidores">Conoce cómo unirte</Link>.
        </p>
      );
    }
    return (
      <p className="ct-direct__note">
        Inicia sesión con tu cuenta de distribución para ver los datos de contacto directo.
      </p>
    );
  };

  return (
    <main className="ct" data-header-theme="light">
      <SEO
        title="Contacto"
        description="Contacta a Physiology: atención a clientas y distribuidoras, facturación y chat en línea."
        path="/contacto"
      />

      <div className="ct-media">
        <img
          src="/images/customer-service/phone.webp"
          alt="Teléfono clásico junto a productos Physiology"
          className="ct-media__img"
        />
      </div>

      <section className="ct-panel">
        <div className="ct-panel__inner">
          <p className="ct-intro">
            Cuéntanos qué necesita tu piel, tu negocio o tu próximo paso.
            Leemos cada mensaje con calma y te responderemos pronto.
          </p>

          <div className="ct-info">
            <p>Teléfono distribución: <a href="tel:+522291059904">229 105 9904</a></p>
            <p>Mail: <a href="mailto:hello@physiologyskincare.com">hello@physiologyskincare.com</a></p>
            <p>Facturación: <a href="mailto:facturacion@physiologyskincare.com">facturacion@physiologyskincare.com</a></p>
          </div>

          <div className="ct-direct">
            <p className="ct-direct__title">
              Atención directa a distribuidores.{' '}
              {!isAuthenticated && !checking && (
                <button type="button" className="ct-link" onClick={() => setShowLogin(true)}>
                  Iniciar sesión.
                </button>
              )}
            </p>
            {renderDistributorBlock()}
          </div>

          <button type="button" className="ct-btn" onClick={openChat}>
            Ayuda inmediata: abrir chat.
          </button>

          <p className="ct-tagline">/La piel que amas empieza con una conversación</p>
          <h1 className="ct-title">Contacto</h1>

          {sent ? (
            <div className="ct-sent">
              <p className="ct-sent__title">Gracias, recibimos tu mensaje.</p>
              <p>Te responderemos lo antes posible al correo que nos dejaste.</p>
              <button type="button" className="ct-link" onClick={() => setSent(false)}>
                Enviar otro mensaje
              </button>
            </div>
          ) : (
            <form className="ct-form" onSubmit={handleSubmit} noValidate>
              <FloatingInput
                name="name"
                label="Tu nombre"
                value={form.name}
                onChange={handleChange}
                error={errors.name}
              />
              <div className="ct-form__row">
                <FloatingInput
                  type="email"
                  name="email"
                  label="Tu correo"
                  value={form.email}
                  onChange={handleChange}
                  error={errors.email}
                />
                <FloatingInput
                  type="tel"
                  name="phone"
                  label="Tu teléfono"
                  value={form.phone}
                  onChange={handleChange}
                />
              </div>
              <FloatingTextarea
                name="message"
                label="Tu mensaje"
                value={form.message}
                onChange={handleChange}
                error={errors.message}
              />
              {sendError && <p className="ct-form__error">{sendError}</p>}
              <button type="submit" className="ct-btn" disabled={sending}>
                {sending ? 'Enviando…' : 'Enviar mensaje'}
              </button>
            </form>
          )}
        </div>
      </section>

      <LoginModal
        isOpen={showLogin}
        onClose={() => setShowLogin(false)}
        onLoginSuccess={() => setShowLogin(false)}
      />
    </main>
  );
};

export default Contacto;
